import { apiService } from './api';
import type { 
  Notification, 
  NotificationListParams, 
  NotificationListResponse, 
  UnreadCountResponse, 
  LinkTelegramRequest 
} from '../types';

export class NotificationService {
  private baseUrl = '/api/notifications';

  /**
   * Get user's notifications with pagination
   */
  async getNotifications(params: NotificationListParams = {}): Promise<NotificationListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    
    const responseData = await apiService.get<{
      notifications: Notification[];
      totalCount: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(endpoint);

    // Get unread count separately
    const unreadCount = await this.getUnreadCount();

    return { 
      notifications: responseData.notifications || [], 
      totalCount: responseData.totalCount || 0, 
      page: responseData.page || 1, 
      pageSize: responseData.pageSize || 10, 
      unreadCount 
    };
  }

  /**
   * Get unread notification count for current user
   */
  async getUnreadCount(): Promise<number> {
    const data = await apiService.get<UnreadCountResponse>(`${this.baseUrl}/unread-count`);
    return data.unreadCount;
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    await apiService.put(`${this.baseUrl}/${notificationId}/read`, {});
  }

  /**
   * Mark all notifications as read for current user
   */
  async markAllAsRead(): Promise<void> {
    await apiService.put(`${this.baseUrl}/mark-all-read`, {});
  }

  /**
   * Link user to Telegram for notifications
   */
  async linkTelegram(telegramUsername: string): Promise<void> {
    const requestData: LinkTelegramRequest = { telegramUsername };
    await apiService.post(`${this.baseUrl}/telegram/link`, requestData);
  }

  /**
   * Unlink user from Telegram
   */
  async unlinkTelegram(): Promise<void> {
    await apiService.delete(`${this.baseUrl}/telegram/unlink`);
  }

  /**
   * Get recent notifications (last 5)
   */
  async getRecentNotifications(): Promise<Notification[]> {
    const response = await this.getNotifications({ page: 1, pageSize: 5 });
    return response.notifications;
  }

  /**
   * Format notification timestamp for display
   */
  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Vừa xong';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} phút trước`;
    } else if (diffInMinutes < 1440) { // 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} giờ trước`;
    } else if (diffInMinutes < 10080) { // 7 days
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} ngày trước`;
    } else {
      return date.toLocaleDateString('vi-VN');
    }
  }

  /**
   * Get notification type display name
   */
  getTypeDisplayName(type?: string | null): string {
    switch (type) {
      case 'Email':
        return 'Email';
      case 'Telegram':
        return 'Telegram';
      case 'InApp':
        return 'Trong ứng dụng';
      case 'System':
        return 'Hệ thống';
      default:
        return 'Thông báo';
    }
  }

  /**
   * Get notification type icon
   */
  getTypeIcon(type?: string | null): string {
    switch (type) {
      case 'Email':
        return '📧';
      case 'Telegram':
        return '📱';
      case 'InApp':
        return '🔔';
      case 'System':
        return '⚙️';
      default:
        return '📢';
    }
  }
}

export const notificationService = new NotificationService();
export default notificationService;
